import axios from 'axios';
import BN from 'bn.js';
import { Connection, ParsedAccountData, PublicKey } from '@solana/web3.js';
import * as anchor from '@project-serum/anchor';
import {
  SOLCHICK_STAKING_FLEXIBLE,
  SOLCHICK_STAKING_LOCKED,
  SOLCHICK_TOKEN_MINT_ON_SOL,
  URL_SERVER_INFO,
} from './solchickConsts';
import ConsoleHelper from './consoleHelper';
import { getSolChicksAssociatedAddress } from './solchickHelper';
import { pubkeyToString, toPublicKey } from './solanaHelper';

export const STATUS_STAKED = 0;
export const STATUS_UNSTAKED = 1;
// export const STATUS_CLAIMED = 2;
// export const STATUS_REWARD = 2;
// export const FLEX_POOL_DAILY_AMOUNT = 25000;
// export const LOCKED_POOL_MONTH4_DAILY_AMOUNT = 25000;
// export const LOCKED_POOL_MONTH8_DAILY_AMOUNT = 25000;
// export const LOCKED_POOL_MONTH12_DAILY_AMOUNT = 25000;

const STAKING_DISABLE_TIME =
  process.env.REACT_APP_CLUSTER === 'mainnet'
    ? new Date('2022-07-01 00:00:00+00:00').getTime()
    : new Date('2022-06-27 00:00:00+00:00').getTime();
export const isStakingDisabled = new Date().getTime() > STAKING_DISABLE_TIME;

export const isClaimDisabled = false;

export const FLEXIBLE_STAKING_PDA_SEED = 'staking';
export const LOCKED_STAKING_PDA_SEED_V2 = 'staking_v2';
export const LOCKED_STAKING_PDA_SEED_V3 = 'staking_v3';
export const LOCKED_USER_STAKING_PDA_SEED_V3 = 'user_staking_v3';

export enum StakeMode {
  FLEXIBLE = 'flexible',
  LOCKED = 'locked',
}

export enum StakeStepMode {
  STAKE = 'stake',
  UNSTAKE = 'unstake',
  REWARD = 'reward',
  RECONCILE = 'reconcile',
}

export enum StakeLockedPoolLength {
  MONTH4 = 1, // 120 days
  MONTH8 = 2, // 240 days
  MONTH12 = 3, // 360 days
}

export const getPoolHandle = (pool: StakeLockedPoolLength | null) =>
  `pool${pool}`;

export function getRewardRate(pool: StakeLockedPoolLength | null) {
  switch (pool) {
    case StakeLockedPoolLength.MONTH12:
      return 60;
    case StakeLockedPoolLength.MONTH8:
      return 45;
    default:
    case StakeLockedPoolLength.MONTH4:
      return 30;
  }
}

export function getLockedPeriod(pool: StakeLockedPoolLength | null) {
  switch (pool) {
    case StakeLockedPoolLength.MONTH12:
      return 360 * 86400;
    case StakeLockedPoolLength.MONTH8:
      return 240 * 86400;
    default:
    case StakeLockedPoolLength.MONTH4:
      return 120 * 86400;
  }
}

export function getUnlockedAmount(
  stakedAmount: number,
  rewardsAmount: number,
  startTime: number,
  pool: StakeLockedPoolLength | null,
) {
  const period = getLockedPeriod(pool);
  const nowTs = Math.floor(new Date().getTime() / 1000);
  if (nowTs < startTime + period) {
    return 0;
  }
  if (nowTs > startTime + period * 2) {
    return stakedAmount + rewardsAmount;
  }
  const pastDays = Math.floor((nowTs - startTime - period) / 86400);
  const periodDays = Math.floor(period / 86400);
  return Math.floor(rewardsAmount / periodDays) * pastDays + stakedAmount;
}

export function getRemainingTime(
  startTime: number,
  pool: StakeLockedPoolLength | null,
) {
  const period = getLockedPeriod(pool);
  const nowTs = Math.floor(new Date().getTime() / 1000);
  if (nowTs < startTime + period) {
    return startTime + period - nowTs;
  }
  return 86400 - ((nowTs - startTime) % 86400);
}

export enum StakeStatusCode {
  NONE = 0,
  START,
  TOKEN_AMOUNT_CHECKING,
  PROCESSING,
  SUBMITTING,
  SUCCESS,
  FAILED = 101,
}

export enum StakeErrorCode {
  NO_ERROR,
  CANT_CONNECT_SOLANA,
  TOKEN_AMOUNT_NOT_ENOUGH,
  STAKE_FAILED,
  SOLANA_NO_ASSOC_ACCOUNT,
  SERVER_INVALID,
  SUBMIT_FAILED,
}

export interface IStakeStatus {
  stake(amount: number): void;
  unstake(amount: number, xAmount: string, handle: string): void;
  reward(handle: string, poolLength?: StakeLockedPoolLength): void;
  isStakeProcessing: boolean;
  stakeStatusCode: StakeStatusCode;
  stakeErrorCode: StakeErrorCode;
  stakeLastError: string | null;
  sourceTxId: string;
}

export interface ILockedUserStakeStatus {
  stakedAmount: number;
  startTime: number;
  claimedAmount: number;
  rewardAmount: number;
}

export const createStakeStatus = (
  stake: (amount: number) => void,
  unstake: (amount: number, xAmount: string, handle: string) => void,
  reward: (handle: string, poolLength?: StakeLockedPoolLength) => void,
  isStakeProcessing: boolean,
  stakeStatusCode = StakeStatusCode.NONE,
  stakeErrorCode: StakeErrorCode,
  stakeLastError: string | null,
  sourceTxId: string,
) => ({
  stake,
  unstake,
  reward,
  isStakeProcessing,
  stakeStatusCode,
  stakeErrorCode,
  stakeLastError,
  sourceTxId,
});

export const getServerInfo = async () => {
  try {
    const result = await axios.get(URL_SERVER_INFO());
    if (result && result.data && result.data.success) {
      return true;
    }
  } catch (e) {
    ConsoleHelper(`getServerInfo: ${e}`);
  }
  return false;
};

export const isEnoughTokenOnSolana = async (
  solanaConnection: Connection,
  address: string,
  amount: string,
) => {
  if (!solanaConnection) {
    return false;
  }
  const bnStakeAmount = new BN(amount);
  const associatedKey = await getSolChicksAssociatedAddress(address);
  ConsoleHelper(
    `isEnoughTokenOnSolana -> associatedKey: ${pubkeyToString(associatedKey)}`,
  );
  let tokenAmount: BN = new BN(0);
  try {
    const parsedAccount = await solanaConnection.getParsedAccountInfo(
      associatedKey,
    );
    if (parsedAccount.value) {
      tokenAmount = new BN(
        (
          parsedAccount.value.data as ParsedAccountData
        ).parsed.info.tokenAmount.amount,
      );
      ConsoleHelper(`isEnoughTokenOnSolana -> parsedAccount`, parsedAccount);
      ConsoleHelper(
        `isEnoughTokenOnSolana -> tokenAmount`,
        tokenAmount.toString(),
      );
    }
  } catch (e) {
    ConsoleHelper(`isEnoughTokenOnSolana: ${e}`);
  }

  if (tokenAmount.cmp(bnStakeAmount) >= 0) {
  } else {
    return false;
  }
  return true;
};

export const getFlexibleUserStakingAccKey = async (
  userWallet: PublicKey,
  handle: string,
) =>
  anchor.web3.PublicKey.findProgramAddress(
    [userWallet.toBuffer(), Buffer.from(handle)] as Array<Buffer | Uint8Array>,
    toPublicKey(SOLCHICK_STAKING_FLEXIBLE),
  );

export const getLockedUserStakingAccV2Key = async (
  userWallet: PublicKey,
  handle: string,
) =>
  anchor.web3.PublicKey.findProgramAddress(
    [userWallet.toBuffer(), Buffer.from(handle)] as Array<Buffer | Uint8Array>,
    toPublicKey(SOLCHICK_STAKING_LOCKED),
  );

export const getLockedUserStakingAccV3Key = async (
  userWallet: PublicKey,
  handle: string,
) =>
  anchor.web3.PublicKey.findProgramAddress(
    [
      toPublicKey(SOLCHICK_TOKEN_MINT_ON_SOL).toBuffer(),
      userWallet.toBuffer(),
      Buffer.from(handle),
      Buffer.from(LOCKED_USER_STAKING_PDA_SEED_V3),
    ] as Array<Buffer | Uint8Array>,
    toPublicKey(SOLCHICK_STAKING_LOCKED),
  );

export const convertLockedUserAccV2ToV3 = (
  pool: StakeLockedPoolLength | null,
  userAccV2: any,
): ILockedUserStakeStatus => {
  const stakedAmount = userAccV2.amount.toNumber();
  const startTime = userAccV2.startTime.toNumber();
  const rewardAmount = Math.round((stakedAmount * getRewardRate(pool)) / 100);
  return {
    stakedAmount,
    startTime,
    claimedAmount: 0,
    rewardAmount,
  };
};

export const parseLockedUserAccV3 = (
  userAccV3: any,
): ILockedUserStakeStatus => ({
  stakedAmount: userAccV3.stakedAmount.toNumber(),
  startTime: userAccV3.startTime.toNumber(),
  claimedAmount: userAccV3.claimedAmount.toNumber(),
  rewardAmount: userAccV3.rewardAmount.toNumber(),
});

export const getLockedStakingPdaV3Key = async (poolHandle: string) =>
  anchor.web3.PublicKey.findProgramAddress(
    [
      toPublicKey(SOLCHICK_TOKEN_MINT_ON_SOL).toBuffer(),
      Buffer.from(LOCKED_STAKING_PDA_SEED_V3),
      Buffer.from(poolHandle),
    ] as Array<Buffer | Uint8Array>,
    toPublicKey(SOLCHICK_STAKING_LOCKED),
  );

export const getFlexibleStakingPdaKey = async () =>
  anchor.web3.PublicKey.findProgramAddress(
    [Buffer.from(FLEXIBLE_STAKING_PDA_SEED)] as Array<Buffer | Uint8Array>,
    toPublicKey(SOLCHICK_STAKING_FLEXIBLE),
  );
